// Préférences d'affichage PAR COMPTE (chantier Van 10/07) — pins, thématiques suivies,
// tailles des graphiques (lot A, 11/10), tuiles et sections masquées (P12, 12/10),
// comparaisons repliées tuile par tuile (14/10).
// Instancié UNE fois dans ObsLayout et partagé via Outlet context (état cohérent
// entre thématiques et vue d'ensemble). Sauvegarde optimiste, échec silencieux
// (les préférences ne doivent jamais casser la lecture).
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useAuth } from '../AuthContext'
import { API, type SectionChart } from './types'

export interface Pin {
  t: string        // thématique (dem, log, …)
  s: string        // id de section (san-1, …)
  k: string        // label du KPI ou titre du chart
  c?: boolean      // true = chart, false/absent = KPI
}

// Lot A (CDC du 09/10, § 3 ; Marc, 11/10 : « par utilisateur, par paliers ») — des PALIERS,
// pas une poignée libre (V2) : l = 1 colonne · 2 colonnes · toute la ligne ; h = standard
// (250 px) · haute (380 px). La taille par défaut ({l: 1, h: 's'}) n'est jamais stockée.
export type Largeur = 1 | 2 | 3
export type Hauteur = 's' | 'h'
export interface TailleGraphique { l: Largeur; h: Hauteur }
export const TAILLE_DEFAUT: TailleGraphique = { l: 1, h: 's' }

// P12 (démo de Besançon, D11 — « pouvoir le masquer à l'échelle de la commune ») : le
// complément de l'épingle. Même identité que `Pin` pour une tuile ou un graphique ; `k` absent =
// la section `s` entière. Par compte ET par commune, comme les épingles (`communes.{insee}`).
export interface Masque {
  t: string
  s: string
  k?: string
  c?: boolean
}

export interface Prefs {
  pins: Pin[]
  /** Ce que le compte a masqué sur CETTE commune — jamais effacé, réaffichable. */
  masques: Masque[]
  thematiques_suivies: string[]
  /** Au COMPTE, pas à la commune : la clé est l'identifiant du graphique (`cleTailleGraphique`). */
  graphiques: Record<string, TailleGraphique>
  /** Repli des comparaisons (Olivier, 14/10) : les tuiles dont le compte ne veut voir que le
      territoire consulté. Au COMPTE, comme les tailles — la clé est `cleTailleGraphique` pour un
      graphique, `cleRepliTuile` pour une tuile ; une tuile absente est dépliée (le défaut). */
  comparaisons_repliees: string[]
}
const VIDE: Prefs = { pins: [], masques: [], thematiques_suivies: [], graphiques: {},
                      comparaisons_repliees: [] }

/** Ce que `ChartCard` lit et écrit — servi par ObsLayout via `TaillesGraphiquesCtx`. */
export interface TaillesGraphiques {
  tailleDe: (cle: string) => TailleGraphique | null
  /** `null` (ou la taille par défaut) retire l'entrée : « Taille par défaut ». */
  fixerTaille: (cle: string, taille: TailleGraphique | null) => void
}

/** Ce que les tuiles et les cartes de graphique lisent et écrivent pour replier leurs comparaisons
    — servi par ObsLayout (et EpciLayout) via `ComparaisonsRepliablesCtx`. */
export interface ComparaisonsRepliables {
  estReplie: (cle: string) => boolean
  /** Replie la tuile, ou la déplie si elle l'est déjà. */
  basculerRepli: (cle: string) => void
}

export interface PrefsApi extends TaillesGraphiques, ComparaisonsRepliables {
  prefs: Prefs
  canPin: boolean
  estEpingle: (p: Pin) => boolean
  togglePin: (p: Pin) => void
  reordonnerPins: (pins: Pin[]) => void
  estMasque: (m: Masque) => boolean
  /** Masque, ou « Afficher de nouveau » si l'élément l'est déjà. */
  basculerMasque: (m: Masque) => void
}

/** Absent (null) hors de l'observatoire connecté — PDF, lien partagé, EPCI, visiteur :
    les cartes n'y proposent aucun réglage et gardent leur taille de toujours. */
export const TaillesGraphiquesCtx = createContext<TaillesGraphiques | null>(null)

/** Absent (null) sans compte connecté, et sur le PDF ou un lien partagé : les tuiles n'y portent
    pas de bouton de repli et montrent toutes leurs comparaisons. */
export const ComparaisonsRepliablesCtx = createContext<ComparaisonsRepliables | null>(null)

/** Longueur maximale d'une clé de tuile, la même côté serveur (`_MAX_CLE_GRAPHIQUE`). */
const MAX_CLE_TUILE = 160

/** La clé de repli d'une tuile (chiffre clé) : sa section et son libellé — les tuiles n'ont pas
    d'identifiant stable, l'épingle et le masque les repèrent déjà ainsi. Préfixée pour ne jamais
    croiser la clé d'un graphique. La même section porte le même nom à la commune et à l'EPCI : le
    choix suit l'indicateur d'une vue à l'autre. `espace` vaut « synthese » pour les grands chiffres. */
export function cleRepliTuile(espace: string, libelle: string): string {
  return `tuile:${espace}|${libelle}`.slice(0, MAX_CLE_TUILE)
}

/** La clé d'un graphique : son identifiant stable « section.clé » (`SectionChart.id`), jamais
    la commune. Repli sur le titre, comme les épingles, pour un graphique qui n'en a pas (la
    courbe « Cap démographique » de la synthèse) — préfixé pour ne jamais croiser un id. */
export function cleTailleGraphique(block: Pick<SectionChart, 'id' | 'title'>): string | null {
  if (block.id) return block.id
  return block.title ? `titre:${block.title}`.slice(0, 160) : null
}

const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/** La réponse du GET, ramenée au contrat (revue codex #688) : un serveur d'avant le lot A, ou
    une réponse partielle (`graphiques: null`, champ absent ou d'un autre type), donne des valeurs
    vides — jamais un champ manquant que `tailleDe` indexerait et qui casserait la page. Seules
    les tailles à un palier connu sont gardées. */
export function normaliserPrefs(brut: unknown): Prefs {
  const p = estObjet(brut) ? brut : {}
  const graphiques: Record<string, TailleGraphique> = {}
  if (estObjet(p.graphiques)) {
    for (const [cle, t] of Object.entries(p.graphiques)) {
      if (estObjet(t) && (t.l === 1 || t.l === 2 || t.l === 3) && (t.h === 's' || t.h === 'h')) {
        graphiques[cle] = { l: t.l, h: t.h }
      }
    }
  }
  // Un masque mal formé (serveur d'avant P12, valeur cassée) est ignoré, jamais appliqué.
  const masques: Masque[] = Array.isArray(p.masques)
    ? (p.masques as unknown[]).filter((m): m is Masque => estObjet(m)
        && typeof m.t === 'string' && typeof m.s === 'string'
        && (m.k === undefined || m.k === null || typeof m.k === 'string'))
      .map(m => (typeof m.k === 'string' ? { t: m.t, s: m.s, k: m.k, c: !!m.c } : { t: m.t, s: m.s }))
    : []
  // Repli des comparaisons : seules les clés lisibles sont gardées, jamais une valeur cassée.
  const comparaisons_repliees = Array.isArray(p.comparaisons_repliees)
    ? (p.comparaisons_repliees as unknown[]).filter((c): c is string => typeof c === 'string' && !!c.trim())
    : []
  return {
    pins: Array.isArray(p.pins) ? p.pins as Pin[] : [],
    masques,
    thematiques_suivies: Array.isArray(p.thematiques_suivies) ? p.thematiques_suivies as string[] : [],
    graphiques,
    comparaisons_repliees,
  }
}

const memePin = (a: Pin, b: Pin) =>
  a.t === b.t && a.s === b.s && a.k === b.k && !!a.c === !!b.c

/** Même élément masqué : la section (sans `k`) ou la tuile exacte (même quadruplet que l'épingle). */
export const memeMasque = (a: Masque, b: Masque) =>
  a.t === b.t && a.s === b.s && (a.k ?? null) === (b.k ?? null)
  && (a.k == null || !!a.c === !!b.c)

// Repli des comparaisons — l'état d'ÉCRITURE de la liste, par compte (son jeton), partagé par tous
// les `usePrefs` : la vue commune (ObsLayout) et la vue EPCI (EpciLayout) en ont chacune un (revue
// codex, 3e passe). `liste` : la dernière liste demandée, celle que le serveur aura une fois la
// file vidée ; `a` : quand elle l'a été (horloge locale) ; `enVol` / `prochaine` : la file, une
// requête à la fois, la dernière liste demandée part au retour de la précédente.
interface EcritureReplis { liste: string[]; a: number; enVol: boolean; prochaine: string[] | null }
const replisDuCompte = new Map<string, EcritureReplis>()
let horlogeReplis = 0

export function usePrefs(insee?: string): PrefsApi {
  const { token, user } = useAuth()
  const [prefs, setPrefs] = useState<Prefs>(VIDE)
  // Le compte dont `prefs` porte les clés de COMPTE (tailles, replis) : son jeton. Revue codex du
  // 14/10 (P2) : un autre compte connecté sans remonter la page voyait ces clés-là jusqu'à son
  // propre GET — et un clic pendant cette fenêtre les écrivait chez lui.
  const compteRef = useRef<string | null>(null)
  // Revue codex (4e passe) : tant que les préférences du COMPTE ne sont pas arrivées (première
  // lecture, autre compte), la liste des replis est inconnue — un repli posé alors attend ici, et
  // s'applique à la liste lue. Sinon il partait sur une liste vide et effaçait celle du compte.
  const replisConnus = useRef(false)
  const replisEnAttente = useRef<string[]>([])
  const envoyerReplisRef = useRef<(liste: string[]) => void>(() => {})

  useEffect(() => {
    if (!token || !user) {
      compteRef.current = null; replisConnus.current = false; replisEnAttente.current = []
      setPrefs(VIDE); return
    }
    const memeCompte = compteRef.current === token
    compteRef.current = token
    if (!memeCompte) { replisConnus.current = false; replisEnAttente.current = [] }
    // Reset IMMÉDIAT au changement de commune (codex 13/07) : sinon les pins de la
    // commune précédente restent affichés le temps du GET — et un toggle pendant
    // cette fenêtre persisterait la liste de l'ANCIENNE commune sous la nouvelle.
    // Les tailles, elles, sont au compte : les garder évite que chaque graphique saute à sa
    // taille par défaut puis revienne le temps du GET.
    // Rien à vider → le MÊME objet : un nouvel objet à chaque passage relançait un rendu, donc
    // l'effet dès que l'identité de `user` change d'un rendu à l'autre — une boucle sans fin
    // (CI de la PR #688 : mémoire épuisée dans les tests dont `useAuth` rend un `user` neuf).
    // Les replis des comparaisons aussi (au compte, 14/10). Un AUTRE compte : tout est vidé.
    const vide = (p: Prefs) => !p.pins.length && !p.masques.length && !p.thematiques_suivies.length
      && !Object.keys(p.graphiques).length && !p.comparaisons_repliees.length
    setPrefs(p => (!memeCompte ? (vide(p) ? p : VIDE)
      : p.pins.length || p.masques.length || p.thematiques_suivies.length
        ? { ...VIDE, graphiques: p.graphiques, comparaisons_repliees: p.comparaisons_repliees } : p))
    let alive = true
    // Un repli demandé (ici ou sur l'autre vue) que ce GET peut ne pas encore refléter : écriture
    // en vol au départ ou à l'arrivée, ou demandée depuis. La liste demandée l'emporte alors.
    const debut = ++horlogeReplis
    const enVolAuDebut = !!replisDuCompte.get(token)?.enVol
    // Épingles PAR COMMUNE (13/07) : le serveur résout les pins de CETTE commune —
    // changer de commune recharge (fin du « mes pins me suivent partout »).
    axios.get<{ prefs?: unknown }>(`${API}/observatoire/me/preferences`,
      { params: insee ? { insee } : undefined,
        headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!alive) return
        const lues = normaliserPrefs(r.data?.prefs)
        const demandee = replisDuCompte.get(token)
        if (demandee && (enVolAuDebut || demandee.enVol || demandee.a > debut)) {
          lues.comparaisons_repliees = demandee.liste
        }
        // Les replis posés pendant le chargement, dans l'ordre, sur la liste lue.
        const attente = replisEnAttente.current
        replisEnAttente.current = []
        for (const c of attente) {
          lues.comparaisons_repliees = lues.comparaisons_repliees.includes(c)
            ? lues.comparaisons_repliees.filter(x => x !== c) : [...lues.comparaisons_repliees, c]
        }
        replisConnus.current = true
        setPrefs(lues)
        if (attente.length) envoyerReplisRef.current(lues.comparaisons_repliees)
      })
      .catch(() => { /* silencieux : lecture avant tout */ })
    return () => { alive = false }
  }, [token, user, insee])

  // Le serveur FUSIONNE (lot A) : une clé absente du corps n'est pas touchée. `envoyer` ne
  // porte donc que ce qui change — un réglage de taille ne réécrit pas les épingles.
  // La promesse rendue ne rejette jamais (échec silencieux) : `envoyerMasques` attend sa fin.
  const envoyer = useCallback((corps: Partial<Prefs>): Promise<void> => {
    if (!token) return Promise.resolve()
    return axios.put(`${API}/observatoire/me/preferences`, { prefs: corps },
      { params: insee ? { insee } : undefined,
        headers: { Authorization: `Bearer ${token}` } }).then(() => {}, () => {})
  }, [token, insee])

  // Revue codex de #723 (P2) : chaque masque envoie la liste COMPLÈTE de la commune. Deux masques
  // posés vite partaient en deux PUT simultanés ([A] puis [A, B]) ; si le premier répondait le
  // dernier, le serveur gardait [A] et B revenait au rechargement. Les écritures d'une commune
  // passent donc une à une : une seule requête en vol, et à son retour (réussie ou non) part la
  // DERNIÈRE liste demandée — les états intermédiaires ne partent jamais. Par compte et commune :
  // une écriture en vol garde la commune (et le jeton) sous lesquels elle a été demandée.
  // Une file par clé de préférence, compte et commune (le repli des comparaisons a la sienne, au
  // compte et partagée entre écrans : `replisDuCompte`).
  const files = useRef(new Map<string, { prochaine: Partial<Prefs> | null }>())
  const envoyerEnFile = useCallback((cle: string, corps: Partial<Prefs>) => {
    const enCours = files.current.get(cle)
    if (enCours) { enCours.prochaine = corps; return }
    const file = { prochaine: null as Partial<Prefs> | null }
    files.current.set(cle, file)
    const suivante = (c: Partial<Prefs>) => {
      void envoyer(c).then(() => {
        const reste = file.prochaine
        file.prochaine = null
        if (reste) suivante(reste)
        else files.current.delete(cle)
      })
    }
    suivante(corps)
  }, [envoyer])
  const envoyerMasques = useCallback((masques: Masque[]) => {
    envoyerEnFile(`masques|${token ?? ''}|${insee ?? ''}`, { masques })
  }, [token, insee, envoyerEnFile])

  const save = useCallback((next: Prefs) => {
    setPrefs(next)
    envoyer({ pins: next.pins, thematiques_suivies: next.thematiques_suivies })
  }, [envoyer])

  const estEpingle = useCallback((p: Pin) => prefs.pins.some(x => memePin(x, p)), [prefs])
  const togglePin = useCallback((p: Pin) => {
    const restants = prefs.pins.filter(x => !memePin(x, p))
    const pins = restants.length === prefs.pins.length ? [...prefs.pins, p] : restants
    save({ ...prefs, pins })
  }, [prefs, save])
  const reordonnerPins = useCallback((pins: Pin[]) => save({ ...prefs, pins }), [prefs, save])

  // Masques (P12) : envoyés seuls — le serveur fusionne, les épingles ne sont pas réécrites.
  const estMasque = useCallback((m: Masque) => prefs.masques.some(x => memeMasque(x, m)), [prefs])
  const basculerMasque = useCallback((m: Masque) => {
    if (!token) return
    const restants = prefs.masques.filter(x => !memeMasque(x, m))
    const masques = restants.length === prefs.masques.length
      ? [...prefs.masques, m.k == null ? { t: m.t, s: m.s } : { t: m.t, s: m.s, k: m.k, c: !!m.c }]
      : restants
    setPrefs({ ...prefs, masques })
    envoyerMasques(masques)
  }, [prefs, token, envoyerMasques])

  const tailleDe = useCallback((cle: string) => prefs.graphiques[cle] ?? null, [prefs])
  const fixerTaille = useCallback((cle: string, taille: TailleGraphique | null) => {
    if (!token) return
    const graphiques = { ...prefs.graphiques }
    if (!taille || (taille.l === TAILLE_DEFAUT.l && taille.h === TAILLE_DEFAUT.h)) {
      delete graphiques[cle]
    } else {
      graphiques[cle] = { l: taille.l, h: taille.h }
    }
    setPrefs({ ...prefs, graphiques })
    envoyer({ graphiques })
  }, [prefs, token, envoyer])

  // Repli des comparaisons (14/10) : au compte, liste entière, envoyée seule et en file (deux
  // replis rapides ne se doublent pas au serveur). Sans commune : la vue EPCI l'enregistre aussi.
  // UNE file par compte, pas par commune ni par écran (revue codex, 2e et 3e passes) : la liste est
  // la même partout, et deux files rendaient au dernier PUT arrivé — pas au dernier demandé — le
  // droit d'écrire. Voir `replisDuCompte`.
  const envoyerReplis = useCallback((liste: string[]) => {
    if (!token) return
    const e = replisDuCompte.get(token) ?? { liste, a: 0, enVol: false, prochaine: null }
    e.liste = liste
    e.a = ++horlogeReplis
    replisDuCompte.set(token, e)
    if (e.enVol) { e.prochaine = liste; return }
    e.enVol = true
    const suivante = (l: string[]) => {
      void envoyer({ comparaisons_repliees: l }).then(() => {
        const reste = e.prochaine
        e.prochaine = null
        if (reste) suivante(reste)
        else e.enVol = false
      })
    }
    suivante(liste)
  }, [token, envoyer])
  useEffect(() => { envoyerReplisRef.current = envoyerReplis }, [envoyerReplis])
  const estReplie = useCallback((cle: string) => prefs.comparaisons_repliees.includes(cle), [prefs])
  const basculerRepli = useCallback((cle: string) => {
    if (!token) return
    const restants = prefs.comparaisons_repliees.filter(c => c !== cle)
    const comparaisons_repliees = restants.length === prefs.comparaisons_repliees.length
      ? [...prefs.comparaisons_repliees, cle] : restants
    setPrefs({ ...prefs, comparaisons_repliees })
    if (!replisConnus.current) { replisEnAttente.current.push(cle); return }
    envoyerReplis(comparaisons_repliees)
  }, [prefs, token, envoyerReplis])

  // Mémoïsé : la valeur passe par un contexte lu par chaque carte de graphique.
  return useMemo(() => ({ prefs, canPin: !!user, estEpingle, togglePin, reordonnerPins,
                          estMasque, basculerMasque, tailleDe, fixerTaille,
                          estReplie, basculerRepli }),
  [prefs, user, estEpingle, togglePin, reordonnerPins, estMasque, basculerMasque, tailleDe,
   fixerTaille, estReplie, basculerRepli])
}
